import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRef, useContext, useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import { useNavigate, Link as RouterLink, useSearchParams, Navigate } from 'react-router-dom';
import { z } from 'zod';
import { AuthService, UserRegistration, UserTypeEnum } from '../../../../../js/api';
import { FormCheckboxField } from '../../../../../js/app/components/FormCheckboxField';
import { FormFeedback } from '../../../../../js/app/components/FormFeedback';
import { FormInputMaskField } from '../../../../../js/app/components/FormInputMaskField';
import { FormTextField } from '../../../../../js/app/components/FormTextField';
import { Logo } from '../../../../../js/app/components/Logo';
import { PasswordField } from '../../../../../js/app/components/PasswordField';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { setFormErrorsFromApiErrors } from '../../../../../js/utils/forms';
import { strongPasswordZodSchema } from '../../../../../js/utils/security';
import { Button } from '../../../components/button';

const validationSchema = z
  .object({
    first_name: z.string().min(1, { message: 'First Name is required' }),
    last_name: z.string().min(1, { message: 'Last Name is required' }),
    phone_number: z.string().min(1, { message: 'Phone Number is required' }),
    password1: strongPasswordZodSchema,
    password2: z.string().min(1, { message: 'Confirm Password is required' }),
    text_opt_in: z.boolean(),
  })
  .refine(({ password1, password2 }) => password1 === password2, {
    path: ['password2'],
    message: "Your passwords don't match.",
  });

export const SourcedProviderRegistration = () => {
  useDocumentTitle('Complete Registration');
  const { setSessionExists } = useContext(SessionContext);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const next = searchParams.get('next');

  const {
    handleSubmit,
    formState: { errors },
    setError,
    register,
    setValue,
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  const mutationRegister = useMutation({
    mutationFn: async (data: UserRegistration) => {
      return AuthService.authRegistrationCreate({ requestBody: data });
    },
  });

  useEffect(() => {
    const prefillMap: Array<['first_name' | 'last_name' | 'phone_number', string | null]> = [
      ['first_name', searchParams.get('first_name')],
      ['last_name', searchParams.get('last_name')],
      ['phone_number', searchParams.get('phone_number')],
    ];
    prefillMap.forEach(([field, value]) => {
      if (value && value.trim().length > 0) {
        setValue(field, value, { shouldValidate: false, shouldDirty: true });
      }
    });
  }, [searchParams, setValue]);

  if (!email) {
    return <Navigate to="/register/provider/setup" replace />;
  }

  const onSubmit = async (data) => {
    try {
      let recaptchaToken = '';
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('registration');
      }

      const response = await mutationRegister.mutateAsync({
        ...data,
        email,
        user_type: UserTypeEnum.PROVIDER,
        recaptcha_token: recaptchaToken,
      } as UserRegistration);

      if (response?.id) {
        setSessionExists();
        navigate(next ?? '/app');
      } else {
        navigate('/register/email-confirmation');
      }
    } catch (errors) {
      setFormErrorsFromApiErrors(errors, setError);
    }
  };

  return (
    <div className="tw:flex-1 tw:w-full tw:min-h-screen tw:flex tw:flex-col tw:p-4 tw:pt-3 tw:sm:bg-brand-green-0 tw:sm:flex-row tw:sm:items-center tw:sm:justify-center tw:sm:p-8">
      <div className="tw:w-full tw:sm:max-w-md tw:sm:mx-auto">
        <div className="tw:bg-zinc-50 tw:rounded-md tw:sm:border tw:sm:border-zinc-200 tw:sm:shadow-sm tw:sm:p-6">
          <div className="tw:flex tw:justify-center tw:mb-4">
            <Logo className="logo" variant="color" />
          </div>
          <h5 className="tw:text-center tw:mb-2 tw:pb-2 text-color-primary-dark">
            Complete Your Registration
          </h5>
          <form
            ref={formRef}
            onSubmit={handleSubmit(onSubmit)}
            className="tw:flex tw:flex-col new-account-form"
          >
            <FormFeedback serverErrors={(mutationRegister.error as any)?.body.non_field_errors} />
            <FormFeedback
              clientError={
                (errors as any).recaptcha_token
                  ? {
                      ...(errors as any).recaptcha_token,
                      message: `reCAPTCHA: ${(errors as any).recaptcha_token.message}`,
                    }
                  : undefined
              }
            />
            <div className="tw:mb-3">
              <div className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1">
                Email
              </div>
              <div className="tw:flex tw:items-center tw:justify-between tw:bg-zinc-100 tw:border tw:border-zinc-200 tw:rounded-md tw:px-3 tw:py-2">
                <span className="tw:text-zinc-700">{email}</span>
                <RouterLink
                  to="/register/provider/setup"
                  className="tw:text-xs tw:text-teal-600 hover:tw:underline"
                >
                  Not you?
                </RouterLink>
              </div>
            </div>
            <div className="tw:grid tw:grid-cols-2 tw:gap-4">
              <FormTextField
                name="first_name"
                label="First Name"
                errors={errors}
                register={register}
                required
              />
              <FormTextField
                name="last_name"
                label="Last Name"
                errors={errors}
                register={register}
                required
              />
            </div>
            <FormInputMaskField
              name="phone_number"
              label="Phone Number"
              mask="(___) ___-____"
              replacement={{ _: /\d/ }}
              placeholder="(123) 456-7890"
              errors={errors}
              register={register}
              required
            />
            <PasswordField
              name="password1"
              label="Password"
              errors={errors}
              register={register}
              required
            />
            <PasswordField
              name="password2"
              label="Confirm Password"
              errors={errors}
              register={register}
              required
            />
            <div className="tw:flex tw:items-start tw:mb-2">
              <FormCheckboxField
                name="text_opt_in"
                label=""
                errors={errors}
                register={register}
                className="tw:mb-0 tw:shrink-0 tw:inline-flex"
              />
              <small className="tw:text-zinc-500 tw:text-sm">
                I consent to receive notifications from Sendit about account activity via SMS.
                Message & data rates may apply. Message frequency depends on account activity. Reply
                STOP to opt out, HELP for help.{' '}
                <a
                  href="https://www.senditgigs.com/terms-of-service/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Use
                </a>{' '}
                &{' '}
                <a
                  href="https://www.senditgigs.com/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </small>
            </div>
            <div className="tw:mt-2">
              <Button
                type="submit"
                color="brandgreen1"
                className="tw:w-full tw:bg-teal-600 tw:text-white tw:py-3 tw:px-4 tw:rounded-lg tw:text-lg tw:font-medium tw:cursor-pointer hover:tw:bg-teal-700 disabled:tw:opacity-50 disabled:tw:cursor-not-allowed"
                disabled={mutationRegister.isLoading}
              >
                Complete Registration
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
